import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

interface SidebarContextValue {
	collapsed: boolean;
	setCollapsed: (value: boolean) => void;
	pageTitle: string;
	setPageTitle: (title: string) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
	const [collapsed, setCollapsed] = useState(() => {
		try {
			return localStorage.getItem("equilityhq_sidebar_collapsed") === "true";
		} catch {
			return false;
		}
	});
	const [pageTitle, setPageTitle] = useState("");

	const handleSetCollapsed = (value: boolean) => {
		setCollapsed(value);
		try {
			localStorage.setItem("equilityhq_sidebar_collapsed", String(value));
		} catch {
			// ignore storage errors
		}
	};

	return (
		<SidebarContext.Provider
			value={{
				collapsed,
				setCollapsed: handleSetCollapsed,
				pageTitle,
				setPageTitle,
			}}
		>
			{children}
		</SidebarContext.Provider>
	);
}

export function useSidebar() {
	const ctx = useContext(SidebarContext);
	if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
	return ctx;
}

export function useAutoCollapseSidebar() {
	const { collapsed, setCollapsed } = useSidebar();
	const previousCollapsed = useRef(collapsed);

	useEffect(() => {
		previousCollapsed.current = collapsed;
		setCollapsed(true);

		return () => {
			setCollapsed(previousCollapsed.current);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
}
